import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { FirCfcsWithQualDefService } from './fir-cfcs-with-qual-def.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-fir-cfcs-with-qual-def',
    templateUrl: './fir-cfcs-with-qual-def.component.html',
    styleUrls: ['./fir-cfcs-with-qual-def.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirCfcsWithQualDefComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();
  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  loading = false;
  showData = false;
  pageTitle = "CFC's with Qualified Deficits";
  columnNames: string[] = [];
  qualDefsData: any[] = [];
  subfCategoryNames: string[] = [];
  subfCategoriesData: any[] = [];
  private isInitialized = false;
  private refreshSub?: Subscription;
  private loadEpoch = 0;
  private readonly contextSubs = new Subscription();

  constructor(
    private service: FirCfcsWithQualDefService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextSubs.unsubscribe();
    this.refreshSub?.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!(this.GENERAL_CONFIG?.base_url || this.baseURLs?.api)) return;
    this.isInitialized = true;
    this.refresh();
  }

  private params(): any {
    return {
      ho_leid: this.filterParams?.ho_leid,
      ho_cdr_no: this.filterParams?.ho_cdr_no,
      ho_reporting_period: this.filterParams?.ho_reporting_period,
      filing_key: this.filterParams?.filing_key,
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year,
      scenario: this.filterParams?.scenario || this.globalService?.scenario
    };
  }

  refresh(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    this.refreshSub?.unsubscribe();
    const epoch = ++this.loadEpoch;
    this.loading = true;
    this.showData = true;
    this.refreshSub = this.service.load(baseUrl, this.params()).subscribe({
      next: (res: any) => {
        if (epoch !== this.loadEpoch) return;
        const data = res?.jsonObject || [];
        this.parse(data);
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        if (epoch !== this.loadEpoch) return;
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private parse(data: any[]): void {
    this.columnNames = [];
    this.qualDefsData = [];
    this.subfCategoryNames = [];
    this.subfCategoriesData = [];
    if (!data.length) return;
    const subf = this.safeJson(data[0]?.GET_SUBF_AMTS_JSON);
    subf.forEach((r: any) => {
      if (r?.CATEGORY_NAME) {
        this.subfCategoryNames.push(r.CATEGORY_NAME);
        this.subfCategoriesData.push(r);
      }
    });
    data.forEach((row: any, i: number) => {
      if (i === 0) return;
      if (this.columnNames.length === 0) {
        this.columnNames = Object.keys(row).filter((c) => {
          const k = c.toUpperCase();
          return k !== 'ENDRATE' && k !== 'REPORTING_PERIOD';
        });
      }
      this.qualDefsData.push(row);
    });
  }

  private safeJson(v: any): any[] {
    try {
      if (!v) return [];
      if (Array.isArray(v)) return v;
      const p = safeJsonInput<any>(v, null, 'fir-cfcs-with-qual-def.parse1');
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
}
